import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { TextInput, Button, ErrorMessage, useScreenQuery } from 'playup-web-core';

import FileInput from '../../../components/FileInput';
import useNotify from '../../../hooks/useNotify';
import paymentStatus from '../../constants/paymentStatus';
import { withdrawalRequest } from '../../../api/withdraw';
import { uploadBankDocument } from '../../../api/users';
import { parseApiError } from '../../../utils/parseApiError';

const WithBankAccount: React.FC<Props> = (props) => {
	const { isSmall } = useScreenQuery();

	// TODO: I am pretty sure this is stored in the user object in context, and we are just prop drilling here.
	const [bankAccount, setBankAccount] = React.useState<any>(props.userBankAccount);
	const { register, handleSubmit, reset, watch } = useForm<FormData>({
		defaultValues: {
			dollars: '10',
		},
	});
	const [uploadForm, setUploadForm] = React.useState<boolean>(false);
	const [newDocument, setNewDocument] = React.useState<FileReader['result']>(null);
	const [sending, setSending] = React.useState<boolean>(false);
	const [errors, setErrors] = React.useState<APIError>();
	const [errorUpload, setErrorUpload] = React.useState<APIError>();
	const notify = useNotify();

	const onSubmit: SubmitHandler<FormData> = (data) => {
		setSending(true);
		setErrors({});
		const amount = Math.round(parseFloat(data.dollars) * 100);

		withdrawalRequest({
			amount,
			user_bank_account_id: bankAccount.id,
		})
			.then(() => {
				setSending(false);
				props.onWithdrawalCreated();
				notify({
					type: 'success',
					title: 'Success',
					message: 'Your withdrawal request was received',
					timeout: 5000,
				});
			})
			.catch((error) => {
				setSending(false);
				setErrors(error.errors);
			});
		reset();
	};

	const uploadDocument = () => {
		if (!newDocument) {
			setErrors({ document: ['Please select a document.'] });
			return;
		}
		setSending(true);
		uploadBankDocument(bankAccount.id, newDocument)
			.then((result: any) => {
				setSending(false);
				setBankAccount({
					...bankAccount,
					status: { ...bankAccount.status, id: result.status?.id },
				});
				setUploadForm(false);
				setErrorUpload(undefined);
				notify({
					type: 'success',
					title: 'Success',
					message: 'Your document was uploaded.',
					timeout: 5000,
				});
			})
			.catch((error) => {
				setSending(false);
				setErrorUpload(error);
			});
	};

	const { dollars } = watch();
	const submitDisabled = parseFloat(dollars) < 10;

	return (
		<>
			{/*TODO: no design provided for this section*/}
			{bankAccount.status.id !== paymentStatus.Approved && props.documentRequired && (
				<>
					{bankAccount.status.id === paymentStatus.Rejected ? (
						<div className="_px-8 _text-center _text-danger">
							<p>Your bank account verification document has been rejected.</p>
						</div>
					) : (
						<div className="_px-8 _text-center _text-warning">
							<p>Your bank account verification document is pending approval.</p>
						</div>
					)}
					{uploadForm ? (
						<form>
							<FileInput
								className="md:_w-full _mt-3 _mx-2 md:_mx-0"
								label="Bank Statement"
								accept=".pdf,.png,.jpg,.jpeg"
								onUpload={setNewDocument}
								error={parseApiError(errors, 'document')}
								{...register('document', { required: true })}
							/>
							<FieldText isSmallScreen={isSmall}>Accepted files: (jpg, png, pdf)</FieldText>
							<div className="_flex _items-center _justify-end _px-2 _pb-4 md:_px-8">
								<Button
									kind="white"
									isLoading={sending}
									className="_w-full _btn _btn-primary-action lg:_mt-0"
									type="button"
									onClick={uploadDocument}
								>
									Upload Document
								</Button>
								<Button kind="danger" onClick={() => setUploadForm(false)}>
									Cancel
								</Button>
							</div>
						</form>
					) : (
						<div className="_flex _items-center _justify-end _px-8 _pb-4">
							<Button kind="white" onClick={() => setUploadForm(true)}>
								UPLOAD NEW DOCUMENT
							</Button>
						</div>
					)}
					{errorUpload && errorUpload.error && <ErrorMessage>{errorUpload.error}</ErrorMessage>}
					<div className="_mx8 _my-4 _border-t _border-gray-200" />
				</>
			)}
			{!uploadForm && (
				<>
					<TextInput
						kind="white"
						label="Amount to Withdraw (AUD)"
						type="number"
						step={0.01}
						placeholder="Minimum $10"
						error={parseApiError(errors, 'amount')}
						{...register('dollars', {
							required: true,
							// onBlur: (e) => {
							// 	setValue('dollars', numeral(e.target.value).format('0.[00]'));
							// },
						})}
					/>
					<BankAccountSection isSmallScreen={isSmall}>
						<BankAccountInfo>
							<Label isSmallScreen={isSmall}>Bank Account Details</Label>
							<BankAccountDetails isSmallScreen={isSmall}>{bankAccount.label}</BankAccountDetails>
						</BankAccountInfo>
					</BankAccountSection>
					<UseNewBankAccountContainer isSmallScreen={isSmall}>
						<LinkText isSmallScreen={isSmall} onClick={props.updateBankAccountClicked}>
							Use new Bank Account
						</LinkText>
						<HintText className=" _whitespace-pre-wrap" isSmallScreen={isSmall} onClick={() => console.log('hi')}>
							Note: your current Bank Account will be updated {'\n'}{' '}
							<span className=" _text-sm">
								Once you submit your withdrawal request, you will not be able to cancel it.
							</span>
						</HintText>
					</UseNewBankAccountContainer>
					<section>
						<StyledButton
							isSmallScreen={isSmall}
							kind="white"
							type="submit"
							disabled={submitDisabled}
							isLoading={sending}
							onClick={handleSubmit(onSubmit)}
						>
							Withdraw
						</StyledButton>
						{errors?.error && <ErrorMessage>{errors.error}</ErrorMessage>}
					</section>
				</>
			)}
		</>
	);
};

export default WithBankAccount;

interface Props {
	userBankAccount: any;
	documentRequired?: boolean;
	onWithdrawalCreated: () => void;
	updateBankAccountClicked: () => void;
}

interface FormData {
	document: FileReader['result'];
	dollars: string;
}

const FieldText = styled.div<{ isSmallScreen: boolean }>`
	color: ${({ theme }) => theme.colors.darkR};
	font-size: ${({ isSmallScreen }) => (isSmallScreen ? '14px' : '18px')};
	margin: ${({ isSmallScreen }) => (isSmallScreen ? '5px 0' : '10px 0')};
`;

const BankAccountSection = styled.section<{ isSmallScreen: boolean }>`
	display: flex;
	justify-content: between;
	align-items: center;
	margin-top: ${({ isSmallScreen }) => (isSmallScreen ? '20px' : '30px')};
`;

const BankAccountInfo = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

const Label = styled.p<{ isSmallScreen: boolean }>`
	font-size: ${({ isSmallScreen }) => (isSmallScreen ? '14px' : '18px')};
	margin-bottom: ${({ isSmallScreen }) => (isSmallScreen ? '5px' : '7px')};
	display: block;
	color: ${({ theme }) => theme.colors.darkR};
	user-select: none;
`;

const BankAccountDetails = styled.p<{ isSmallScreen: boolean }>`
	display: flex;
	align-items: center;
	border-radius: 5px;
	border: 2px solid ${({ theme }) => theme.colors.lightGreyR};
	height: ${({ isSmallScreen }) => (isSmallScreen ? '50px' : '70px')};
	padding: 10px;
`;

const UseNewBankAccountContainer = styled.div<{ isSmallScreen: boolean }>`
	display: flex;
	flex-direction: column;
	margin-top: ${({ isSmallScreen }) => (isSmallScreen ? '10px' : '20px')};
`;

const HintText = styled.p<{ isSmallScreen: boolean }>`
	color: ${({ theme }) => theme.colors.darkR};
	font-size: ${({ isSmallScreen }) => (isSmallScreen ? '12px' : '16px')};
	text-align: center;
	margin-bottom: ${({ isSmallScreen }) => (isSmallScreen ? '5px' : '7px')};
`;

const LinkText = styled.p<{ isSmallScreen: boolean }>`
	color: ${({ theme }) => theme.colors.blue};
	text-decoration: underline;
	font-size: ${({ isSmallScreen }) => (isSmallScreen ? '12px' : '16px')};
	text-align: center;
	margin-bottom: ${({ isSmallScreen }) => (isSmallScreen ? '5px' : '7px')};
	cursor: pointer;
`;

const StyledButton = styled(Button)<{ isSmallScreen: boolean }>`
	margin-top: ${({ isSmallScreen }) => (isSmallScreen ? '10px' : '20px')};
	margin-bottom: ${({ isSmallScreen }) => (isSmallScreen ? '10px' : '20px')};
`;
